// src/components/facilityTransfer/FacilityTransferClaim.jsx
//
// /facility-transfer/:token - the link in "Prenez possession de votre
// établissement". Shows the facility before any login, then the owner logs
// in (or creates their account) with the invited e-mail and accepts:
// the facility becomes theirs, with 30 days of free evaluation
// (backend utils/facilityTransfer.ts).
import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Container, Typography, Paper, Button, Alert, CircularProgress, Box, Stack } from '@mui/material';
import { initKeycloak } from '../../keycloak';
import { getClaim, acceptClaim, transferErrorText } from '../../utils/facilityTransferApi';

const day = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
const STATUS_ERROR = { EXPIRED: 'TRANSFER_EXPIRED', CANCELLED: 'TRANSFER_CANCELLED', ACCEPTED: 'TRANSFER_ACCEPTED' };

export default function FacilityTransferClaim() {
  const { token } = useParams();
  const [claim, setClaim] = useState(null);
  const [kc, setKc] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    getClaim(token).then(setClaim).catch((e) => setError(transferErrorText(e)));
    initKeycloak().then((k) => {
      window.history.replaceState(null, '', window.location.origin + window.location.pathname);
      setKc(k);
    }).catch(console.error);
  }, [token]);

  if (error && !claim) return <Container maxWidth="sm" sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!claim || !kc) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  const loggedInAs = kc.authenticated ? (kc.tokenParsed?.email ?? '').toLowerCase() : null;
  const rightAccount = loggedInAs === claim.toEmail.toLowerCase();
  const back = window.location.href;
  const login = () => kc.login({ redirectUri: back, loginHint: claim.toEmail });
  const register = () => kc.register({ redirectUri: back, loginHint: claim.toEmail });
  const switchAccount = () => kc.logout({ redirectUri: back });

  const accept = async () => {
    setBusy(true); setError('');
    try {
      await kc.updateToken(30).catch(() => {});
      setDone(await acceptClaim(token, kc.token));
    } catch (e) { setError(transferErrorText(e)); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="success" sx={{ mb: 2 }}><b>{claim.facility.name}</b> est maintenant à vous.</Alert>
        <Typography sx={{ mb: 2 }}>
          Votre essai gratuit court jusqu’au {day(done.endOfEvaluation)}. La borne continue de fonctionner normalement ;
          vous retrouvez les avis et les statistiques dans votre tableau de bord.
        </Typography>
        <Button variant="contained" component={RouterLink} to="/showDashboard">Ouvrir mon tableau de bord</Button>
      </Container>
    );
  }

  const closed = STATUS_ERROR[claim.status];
  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Prenez possession de votre établissement</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{claim.facility.name}</Typography>
        {claim.facility.address && <Typography color="text.secondary">{claim.facility.address}</Typography>}
        <Typography variant="body2" sx={{ mt: 1 }}>Installé par {claim.invitedBy}.</Typography>
      </Paper>

      {closed ? (
        <Alert severity="warning">{transferErrorText(new Error(closed))}</Alert>
      ) : (
        <>
          <Typography sx={{ mb: 2 }}>
            En acceptant, l’établissement rejoint votre compte QRProofs avec 30 jours d’essai gratuit.
            Invitation pour <b>{claim.toEmail}</b>, valable jusqu’au {day(claim.expiresAt)}.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!loggedInAs && (
            <Stack spacing={1}>
              <Button variant="contained" size="large" onClick={login}>Me connecter avec {claim.toEmail}</Button>
              <Button variant="outlined" onClick={register}>Créer mon compte</Button>
            </Stack>
          )}
          {loggedInAs && !rightAccount && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Vous êtes connecté en tant que {loggedInAs}. Cette invitation est pour {claim.toEmail} :
                connectez-vous avec ce compte.
              </Alert>
              <Button variant="contained" onClick={switchAccount}>Changer de compte</Button>
            </>
          )}
          {rightAccount && (
            <Button variant="contained" size="large" fullWidth disabled={busy} onClick={accept} sx={{ py: 1.5, fontWeight: 700 }}>
              Accepter l’établissement
            </Button>
          )}
        </>
      )}
    </Container>
  );
}
